import React, { ReactNode, useEffect } from 'react';
import { Dialog, DialogActionsBar, DialogHandle, DialogProps } from '@progress/kendo-react-dialogs';
import cn from 'clsx';
import Button from '@components-clean/core/button';
import { useTranslation } from '@hooks/index';
import { setAttribute } from '@utils/automation-test-helpers';

export interface ConfirmationDialogProps extends DialogProps {
    dataTestId: string;
    onClose?: (event?: unknown) => void;
    onSubmit?: (event?: unknown) => void;
    primaryBtnLabel?: string;
    secondaryBtnLabel?: string;
    primaryBtnDisabled?: boolean;
    createdUpdatedInfo?: ReactNode;
    closeIcon?: boolean;
    hideSecondaryBtn?: boolean;
    ignoreReadonlyCtx?: boolean;
}

const ConfirmationDialog = ({
    dataTestId,
    title,
    primaryBtnLabel,
    primaryBtnDisabled,
    secondaryBtnLabel,
    onClose,
    onSubmit,
    children,
    className,
    createdUpdatedInfo,
    closeIcon,
    hideSecondaryBtn,
    ignoreReadonlyCtx,
    ...rest
}: ConfirmationDialogProps) => {
    const { t } = useTranslation();
    const dialogRef = React.useRef<DialogHandle | null>(null);

    useEffect(() => setAttribute(dialogRef, 'data-testid', dataTestId));

    return (
        <Dialog
            title={title}
            onClose={onClose}
            closeIcon={closeIcon}
            minWidth={450}
            ref={dialogRef}
            autoFocusedElement="#disableAutoFocus"
            {...rest}
        >
            <div
                className={cn('k-h-full k-d-flex k-align-items-center k-h-1 k-justify-content-center', className)}
                style={{ minHeight: 65 }}
            >
                {children}
            </div>
            <DialogActionsBar layout="end">
                {createdUpdatedInfo}
                {onClose && !hideSecondaryBtn && (
                    <Button
                        onClick={() => (onClose ? onClose() : {})}
                        data-testid={`${dataTestId}-cancel-button`}
                        ignoreReadonlyCtx={ignoreReadonlyCtx}
                        disableFocus
                    >
                        {secondaryBtnLabel ?? t('cancel')}
                    </Button>
                )}
                {onSubmit && (
                    <Button
                        onClick={onSubmit}
                        data-testid={`${dataTestId}-save-submit`}
                        primary
                        disabled={primaryBtnDisabled}
                        ignoreReadonlyCtx={ignoreReadonlyCtx}
                    >
                        {primaryBtnLabel ?? t('confirm')}
                    </Button>
                )}
            </DialogActionsBar>
        </Dialog>
    );
};

export default ConfirmationDialog;
